import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { api, unwrap } from '@/lib/api';
import { AdminButton } from './button';
import { AdminIcon } from './icon';

/**
 * P19 §11 — Éligibilité njangi + grille de paliers, dans la fiche produit.
 * L'aperçu montre le prix OBTENU à chaque palier et alerte sous le prix de
 * revient (R6 : marge négative sur palier mal saisi). La grille est l'affaire
 * de l'admin — le créateur d'une campagne ne choisit jamais un prix.
 */

type Grid = {
  eligible: boolean;
  basePrice: number;
  costPrice: number | null;
  tiers: Array<{
    minThreshold: number;
    discountPct: number | null;
    unitPrice: number | null;
    resolvedPrice: number;
    belowCost: boolean;
  }>;
};

type Row = { minThreshold: number; mode: 'pct' | 'price'; value: number };

const fmtF = (n: number) => `${n.toLocaleString('fr-FR')} F`;

export function NjangiTiersEditor({ productId }: { productId: string }) {
  const qc = useQueryClient();
  const grid = useQuery({
    queryKey: ['njangi-tiers', productId],
    queryFn: () => unwrap<Grid>(api.get(`/admin/catalog/products/${productId}/njangi-tiers`)),
  });

  const [eligible, setEligible] = useState(false);
  const [rows, setRows] = useState<Row[]>([]);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!grid.data) return;
    setEligible(grid.data.eligible);
    setRows(
      grid.data.tiers.map((t) => ({
        minThreshold: t.minThreshold,
        mode: t.unitPrice != null ? 'price' : 'pct',
        value: t.unitPrice ?? t.discountPct ?? 0,
      })),
    );
  }, [grid.data]);

  const save = useMutation({
    mutationFn: () =>
      unwrap(
        api.put(`/admin/catalog/products/${productId}/njangi-tiers`, {
          eligible,
          tiers: rows.map((r, i) => ({
            minThreshold: r.minThreshold,
            discountPct: r.mode === 'pct' ? r.value : undefined,
            unitPrice: r.mode === 'price' ? r.value : undefined,
            sortOrder: i,
          })),
        }),
      ),
    onSuccess: () => {
      setError(null);
      void qc.invalidateQueries({ queryKey: ['njangi-tiers', productId] });
    },
    onError: (e: { response?: { data?: { errors?: string[] } } }) =>
      setError(e.response?.data?.errors?.join(' ') ?? 'Enregistrement impossible.'),
  });

  const base = grid.data?.basePrice ?? 0;
  const cost = grid.data?.costPrice ?? null;
  const preview = (r: Row) =>
    r.mode === 'price' ? r.value : Math.round((base * (1 - r.value / 100)) / 500) * 500;

  return (
    <div style={{ border: '1px solid var(--a-border)', borderRadius: 10, padding: 16, marginTop: 16 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div>
          <div style={{ fontWeight: 700 }}>Njangi · achat groupé</div>
          <div className="muted" style={{ fontSize: 12 }}>
            Paliers de prix de gros — le créateur d'un groupe choisit une cible, jamais un prix.
          </div>
        </div>
        <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13 }}>
          <input
            type="checkbox"
            checked={eligible}
            onChange={(e) => setEligible(e.target.checked)}
          />
          Éligible au njangi
        </label>
      </div>

      {error && (
        <p style={{ color: 'var(--a-danger)', fontSize: 13, margin: '10px 0 0' }}>{error}</p>
      )}

      {eligible && (
        <>
          <table className="table" style={{ fontSize: 12, marginTop: 12 }}>
            <thead>
              <tr>
                <th>Dès (seuil)</th>
                <th>Type</th>
                <th className="right">Valeur</th>
                <th className="right">Prix obtenu</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r, i) => {
                const p = preview(r);
                return (
                  <tr key={i}>
                    <td>
                      <input
                        type="number"
                        min={2}
                        value={r.minThreshold}
                        onChange={(e) =>
                          setRows((rs) =>
                            rs.map((x, j) =>
                              j === i ? { ...x, minThreshold: Number(e.target.value) } : x,
                            ),
                          )
                        }
                        className="aform-i aform-i-xs"
                        style={{ width: 80 }}
                      />
                    </td>
                    <td>
                      <select
                        value={r.mode}
                        onChange={(e) =>
                          setRows((rs) =>
                            rs.map((x, j) =>
                              j === i ? { ...x, mode: e.target.value as Row['mode'] } : x,
                            ),
                          )
                        }
                        className="aform-i aform-i-xs"
                      >
                        <option value="pct">Remise %</option>
                        <option value="price">Prix ferme</option>
                      </select>
                    </td>
                    <td className="right">
                      <input
                        type="number"
                        min={r.mode === 'pct' ? 1 : 100}
                        value={r.value}
                        onChange={(e) =>
                          setRows((rs) =>
                            rs.map((x, j) =>
                              j === i ? { ...x, value: Number(e.target.value) } : x,
                            ),
                          )
                        }
                        className="aform-i aform-i-xs"
                        style={{ width: 110, textAlign: 'right' }}
                      />
                    </td>
                    <td
                      className="right"
                      style={{
                        fontVariantNumeric: 'tabular-nums',
                        color: cost !== null && p < cost ? 'var(--a-danger)' : undefined,
                        fontWeight: 600,
                      }}
                      title={
                        cost !== null && p < cost
                          ? `⚠️ Sous le prix de revient (${fmtF(cost)})`
                          : undefined
                      }
                    >
                      {fmtF(p)}
                      {cost !== null && p < cost ? ' ⚠️' : ''}
                    </td>
                    <td className="right">
                      <button
                        type="button"
                        className="top-btn"
                        onClick={() => setRows((rs) => rs.filter((_, j) => j !== i))}
                        style={{ color: 'var(--a-danger)' }}
                      >
                        <AdminIcon n="close" s={12} />
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
            <AdminButton
              variant="ghost"
              size="sm"
              onClick={() =>
                setRows((rs) => [
                  ...rs,
                  { minThreshold: (rs.at(-1)?.minThreshold ?? 1) + 2, mode: 'pct', value: 7 },
                ])
              }
            >
              + Palier
            </AdminButton>
            <AdminButton size="sm" onClick={() => save.mutate()} disabled={save.isPending}>
              {save.isPending ? '…' : 'Enregistrer la grille'}
            </AdminButton>
          </div>
        </>
      )}
      {!eligible && grid.data?.eligible === false && rows.length === 0 && (
        <p className="muted" style={{ fontSize: 12, marginTop: 8 }}>
          Produit fermé à l'achat groupé. Coche « Éligible » et pose au moins un palier
          pour l'ouvrir.
        </p>
      )}
      {!eligible && grid.data?.eligible && (
        <AdminButton size="sm" onClick={() => save.mutate()} style={{ marginTop: 10 }}>
          Enregistrer (désactiver)
        </AdminButton>
      )}
    </div>
  );
}
